import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Loader, CheckCircle, AlertTriangle, XCircle, Download } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { festivalsAPI, handleApiError } from '../utils/api';

const STATUS_STYLES = {
  CREATED: { icon: CheckCircle, className: 'text-green-700 bg-green-50 border-green-200', label: 'Importé' },
  SKIPPED: { icon: AlertTriangle, className: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Ignoré' },
  ERROR: { icon: XCircle, className: 'text-red-700 bg-red-50 border-red-200', label: 'Erreur' },
};

/**
 * Import d'un ou plusieurs festivals (avec séries, tournois et programme)
 * depuis un fichier JSON. Format : voir le fichier d'exemple.
 */
const ImportFestivalsPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [fileName, setFileName] = useState('');
  const [payload, setPayload] = useState(null);
  const [summary, setSummary] = useState('');
  const [parseError, setParseError] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-2 rounded-lg">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    setResults(null);
    setError(null);
    setParseError(null);
    setPayload(null);
    setSummary('');
    setFileName(file ? file.name : '');
    if (!file) return;
    try {
      const json = JSON.parse(await file.text());
      const list = Array.isArray(json) ? json : json.festivals || (json.title ? [json] : null);
      if (!list || list.length === 0) {
        setParseError('Aucun festival trouvé dans ce fichier (attendu : { "festivals": [ ... ] }).');
        return;
      }
      const tournaments = list.reduce((n, f) => n + (f.tournaments ? f.tournaments.length : 0), 0);
      setSummary(`${list.length} festival${list.length > 1 ? 's' : ''}, ${tournaments} tournoi${tournaments > 1 ? 's' : ''}`);
      setPayload(json);
    } catch (err) {
      setParseError(`Fichier JSON invalide : ${err.message}`);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    setError(null);
    setResults(null);
    try {
      const response = await festivalsAPI.importFestivals(payload);
      setResults(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsImporting(false);
    }
  };

  const created = results ? results.filter((r) => r.status === 'CREATED').length : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/manage-festivals')}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à la gestion des festivals
          </button>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center mb-2">
              <Upload className="mr-3" size={28} />
              Importer des festivals
            </h1>
            <p className="text-gray-600 mb-4">
              Choisis un fichier JSON contenant un ou plusieurs festivals avec leurs séries et tous leurs
              tournois (dates, buy-in, structure, programme...). Un festival déjà présent (même titre et
              mêmes dates) est ignoré. Le circuit et les lieux cités doivent déjà exister.
            </p>
            <a
              href="/exemple-import-festivals.json"
              download
              className="inline-flex items-center text-sm text-blue-600 hover:underline mb-6"
            >
              <Download size={16} className="mr-1" />
              Télécharger un fichier d'exemple
            </a>

            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFile}
              className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg p-2 mb-3"
            />

            {parseError && <p className="text-red-600 text-sm mb-3">{parseError}</p>}
            {payload && (
              <p className="text-sm text-gray-700 mb-4">
                <span className="font-semibold">{fileName}</span> : {summary}
              </p>
            )}

            <button
              onClick={handleImport}
              disabled={!payload || isImporting}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center disabled:opacity-50"
            >
              {isImporting ? <Loader className="mr-2 animate-spin" size={20} /> : <Upload className="mr-2" size={20} />}
              {isImporting ? 'Import en cours...' : 'Importer'}
            </button>

            {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
          </div>

          {results && (
            <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
              <h2 className="text-lg font-bold text-gray-800 mb-3">
                Résultat : {created} festival{created > 1 ? 's' : ''} importé{created > 1 ? 's' : ''} sur {results.length}
              </h2>
              <ul className="space-y-2">
                {results.map((r, i) => {
                  const style = STATUS_STYLES[r.status] || STATUS_STYLES.ERROR;
                  const Icon = style.icon;
                  return (
                    <li key={i} className={`border rounded-lg p-3 flex items-start gap-3 ${style.className}`}>
                      <Icon size={20} className="flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold">
                          {r.title} <span className="font-normal">· {style.label}</span>
                        </p>
                        <p className="text-sm">
                          {r.status === 'CREATED'
                            ? `${r.tournamentsCount} tournoi(s), ${r.seriesCount} série(s)`
                            : r.message}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {created > 0 && (
                <button
                  onClick={() => navigate('/manage-festivals')}
                  className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  Voir les festivals
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportFestivalsPage;
